import { X } from "lucide-react";
import React, { useState } from "react";
import { updateSimSwapStatus } from "../../actions/SimSwapAction";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { formatDate, formatDateOnly } from "../../utils/DateFormat";
import StatusEditForm from "../layout/StatusEditorForm";
import { useSession } from "../layout/SessionContext";
import { PhoneNumberUtil } from "google-libphonenumber";

const DetailBox = ({
  closeModal,
  selectedSimSwap,
  getStatusClass,
  SIMSWAP_STATUSES,
  formatStatus,
}) => {
  const { hasPermission } = useSession();
  const [isEditingStatus, setIsEditingStatus] = useState(false);
  const refreshPage = () => {
    window.location.reload();
  };
  const handleSaveStatus = async (simSwapId, status, statusNote) => {
    try {
      const response = await updateSimSwapStatus(simSwapId, status, statusNote);

      if (response.success) {
        toast.success(response?.message || "Status updated successfully");
        setIsEditingStatus(false);
        refreshPage();
      } else {
        toast.error(response?.message || "Failed to update status");
      }
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    }
  };

  const formatMobileNumber = (mobileNumber) => {
    if (!mobileNumber) return "N/A";

    try {
      const phoneUtil = PhoneNumberUtil.getInstance();
      const cleanNumber = mobileNumber.toString().replace(/[+\s]/g, "");
      const internationalNumber = cleanNumber.startsWith("+")
        ? cleanNumber
        : `+${cleanNumber}`;

      const number = phoneUtil.parseAndKeepRawInput(internationalNumber, "US");

      if (phoneUtil.isValidNumber(number)) {
        const countryCode = number.getCountryCode();
        const nationalNumber = number.getNationalNumber();

        return `(+${countryCode}) ${nationalNumber}`;
      }
    } catch (error) {
      console.warn("Error parsing mobile number:", error);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
      <div
        className="bg-white rounded-[20px] max-h-[90vh] p-4 md:p-6 relative flex flex-col w-[90vw] md:w-[800px]"
        style={{ maxWidth: "800px", width: "90vw" }}
      >
        <button
          onClick={closeModal}
          className="absolute top-2 right-2 md:-top-5 md:-right-10 bg-white rounded-full p-2 shadow-md"
        >
          <X className="w-6 h-6 text-gray-600 hover:text-gray-800" />
        </button>
        <div>
          <h2 className="text-2xl md:text-3xl font-bold mb-3 text-text1">
            Sim swap
          </h2>
          <p className="text-xs md:text-sm text-gray-500 font-normal">
            Status:{" "}
            <span className={getStatusClass(selectedSimSwap.status)}>
              {formatStatus(selectedSimSwap.status)}
            </span>
          </p>
        </div>
        <hr className="mt-4" />
        <div
          className="overflow-y-auto flex-1 pr-2"
          style={{
            msOverflowStyle: "none",
            scrollbarWidth: "none",
          }}
        >
          {/* Hide webkit scrollbar */}
          <style jsx>{`
            div::-webkit-scrollbar {
              display: none;
            }
          `}</style>

          <div className="mb-3 mt-6">
            {[
              { label: "ID", value: selectedSimSwap.uid },
              { label: "Date & Time", value: selectedSimSwap.createdAt },
              {
                label: "Mobile Number",
                value: `${
                  selectedSimSwap.customer.mobileNumber
                    ? `${formatMobileNumber(
                        selectedSimSwap.customer.mobileNumber
                      )}`
                    : "N/A"
                }`,
              },
              {
                label: "Email address for notification ",
                value: selectedSimSwap.deliveredToEmail,
              },

              {
                label: "Terms and conditions",
                value: "Accepted",
              },
              { label: "Remark", value: selectedSimSwap.remark },
            ].map(({ label, value }) => (
              <div key={label} className="flex justify-between py-3">
                <p className="md:font-medium font-normal md:text-base text-[14px] text-text2">
                  {label}
                </p>
                <p className="md:font-medium font-normal md:text-base text-[14px] text-text1 text-right">
                  {label === "Date & Time" ? formatDate(value) : value || "N/A"}
                </p>
              </div>
            ))}
            {isEditingStatus ? (
              <StatusEditForm
                statuses={SIMSWAP_STATUSES}
                setEditing={setIsEditingStatus}
                currentStatus={selectedSimSwap.status}
                handleSaveStatus={handleSaveStatus}
                Id={selectedSimSwap._id}
                requireStatusNote={false}
              />
            ) : (
              selectedSimSwap.status && (
                <div className="flex justify-between py-3">
                  <p className="md:font-medium md:text-base text-[14px] font-normal text-text2">
                    Status
                    {hasPermission("Sim swap", "edit") && (
                      <button
                        onClick={() => setIsEditingStatus(true)}
                        className="ml-2 text-[#4B5563] text-sm bg-[#E5E7EB] px-3 py-0.5 rounded-xl"
                      >
                        Change
                      </button>
                    )}
                  </p>
                  <div className={getStatusClass(selectedSimSwap.status)}>
                    {formatStatus(selectedSimSwap.status)}
                  </div>
                </div>
              )
            )}
          </div>
          {selectedSimSwap.note && (
            <div className="flex justify-between py-3">
              <p className="font-medium text-base text-text2">Note</p>
              <p className="font-medium text-base text-text1 text-right">
                {selectedSimSwap.note}
              </p>
            </div>
          )}
          <hr />
          <div>
            <div className="flex justify-between pt-3 pb-2.5">
              <p className="md:font-medium font-bold text-base text-text1">
                SIM Details
              </p>
            </div>
            {[
              {
                label: "Customer old number",
                value: `+${selectedSimSwap.oldMobileNumber}`,
              },
              {
                label: "First name",
                value: `${selectedSimSwap.customerFirstName}`,
              },
              {
                label: "Last name",
                value: selectedSimSwap.customerLastName,
              },
              {
                label: "Date of Birth",
                value: formatDateOnly(selectedSimSwap.dateOfBirth) || "N/A",
              },
              {
                label: "PUK Code",
                value: selectedSimSwap.pukCode,
              },
              {
                label: "New Sim Serial Number",
                value: selectedSimSwap.newSimSerialNumber,
              },
            ].map(({ label, value }) => (
              <div key={label} className="flex justify-between py-3">
                <p className="md:font-medium font-normal md:text-base text-[14px] text-text2 whitespace-nowrap shrink-0">
                  {label}
                </p>
                <p className="md:font-medium font-normal md:text-base text-[14px] text-text1 text-right break-words min-w-0">
                  {value || "N/A"}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DetailBox;
